/**
 * IVariant - Interface para variantes de ComplexRole
 *
 * Cada variante complexa (hex, rgb:object, hsl:string) implementa esta interface.
 * Usada quando variantes têm tipos/estruturas diferentes.
 *
 * @example
 * class HexVariant implements IVariant<string, RGBA> { ... }
 * class RgbObjectVariant implements IVariant<RGBA, RGBA> { ... }
 */

import type { ValidationResult, BaseFormatOptions } from "../types";
import type { Result } from "./IRole";

// =============================================================================
// IVARIANT INTERFACE
// =============================================================================

/**
 * Interface para uma variante de ComplexRole.
 *
 * @typeParam TValue - Tipo do valor nesta variante (ex: string para hex, RGBA para rgb:object)
 * @typeParam TBase - Tipo da base da role (ex: RGBA para color)
 *
 * @example
 * // Variante hex de Color
 * class HexVariant implements IVariant<string, RGBA> {
 *   name = 'hex';
 *   type = 'string';
 *
 *   toBase(hex: string): RGBA { ... }
 *   fromBase(rgba: RGBA): string { ... }
 *   cast(input: unknown): string | null { ... }
 *   validate(hex: string): ValidationResult { ... }
 *   format(hex: string, opts?): string { ... }
 * }
 */
export interface IVariant<TValue = unknown, TBase = unknown> {
  // ===========================================================================
  // METADATA
  // ===========================================================================

  /** Nome da variante (ex: "hex", "rgb:object", "hsl:string") */
  readonly name: string;

  /** Tipo primitivo do valor (ex: "string", "object") */
  readonly type: "string" | "number" | "boolean" | "object" | "array";

  // ===========================================================================
  // CONVERT
  // ===========================================================================

  /**
   * Converte valor desta variante para o formato base da role.
   *
   * @example
   * hexVariant.toBase('#ff0000') // { r: 255, g: 0, b: 0, a: 1 }
   */
  toBase(value: TValue): TBase;

  /**
   * Converte do formato base para esta variante.
   *
   * @example
   * hexVariant.fromBase({ r: 255, g: 0, b: 0, a: 1 }) // '#ff0000ff'
   */
  fromBase(base: TBase): TValue;

  // ===========================================================================
  // CAST
  // ===========================================================================

  /**
   * Tenta normalizar um input desconhecido para o tipo desta variante.
   * Retorna null se não conseguir.
   *
   * @example
   * hexVariant.cast('ff0000')        // '#ff0000'
   * hexVariant.cast('red')           // '#ff0000'
   * hexVariant.cast('rgb(255,0,0)')  // '#ff0000'
   * hexVariant.cast(123)             // null
   */
  cast(input: unknown): TValue | null;

  /**
   * Versão safe que retorna Result
   */
  tryCast(input: unknown): Result<TValue>;

  // ===========================================================================
  // VALIDATE
  // ===========================================================================

  /**
   * Valida se um valor atende às regras desta variante.
   *
   * @example
   * hexVariant.validate('#ff0000')   // { valid: true, errors: [] }
   * hexVariant.validate('#gg0000')   // { valid: false, errors: ['Invalid hex character'] }
   */
  validate(value: TValue): ValidationResult;

  /**
   * Versão simplificada que retorna boolean
   */
  isValid(value: TValue): boolean;

  // ===========================================================================
  // FORMAT
  // ===========================================================================

  /**
   * Formata valor para apresentação humana.
   *
   * @example
   * hexVariant.format('#ff0000')                      // '#ff0000'
   * hexVariant.format('#ff0000', { uppercase: true }) // '#FF0000'
   */
  format(value: TValue, options?: BaseFormatOptions): string;
}

// =============================================================================
// ABSTRACT BASE CLASS
// =============================================================================

/**
 * Classe base abstrata que implementa métodos auxiliares comuns.
 * Variantes concretas podem estender esta classe.
 */
export abstract class BaseVariant<TValue = unknown, TBase = unknown>
  implements IVariant<TValue, TBase>
{
  abstract readonly name: string;
  abstract readonly type: "string" | "number" | "boolean" | "object" | "array";

  abstract toBase(value: TValue): TBase;
  abstract fromBase(base: TBase): TValue;
  abstract cast(input: unknown): TValue | null;
  abstract validate(value: TValue): ValidationResult;
  abstract format(value: TValue, options?: BaseFormatOptions): string;

  /**
   * Implementação padrão de tryCast usando cast
   */
  tryCast(input: unknown): Result<TValue> {
    const result = this.cast(input);
    if (result === null) {
      return {
        ok: false,
        error: `Cannot cast "${String(input)}" to ${this.name}`,
      };
    }
    return { ok: true, value: result };
  }

  /**
   * Implementação padrão de isValid usando validate
   */
  isValid(value: TValue): boolean {
    return this.validate(value).valid;
  }
}

// =============================================================================
// TYPE HELPERS
// =============================================================================

/**
 * Extrai o tipo de valor de uma variante
 */
export type VariantValueType<V extends IVariant> =
  V extends IVariant<infer TValue, unknown> ? TValue : never;

/**
 * Extrai o tipo base de uma variante
 */
export type VariantBaseType<V extends IVariant> =
  V extends IVariant<unknown, infer TBase> ? TBase : never;
